import { Component, Prop, h, Element, State, Method } from '@stencil/core';
import { ACTION_REGISTRY } from '../../../../../actions/_generated-registry';
import type { ActionName } from '../../../../../actions/_generated-registry';
import type { ActionContext } from '../../../../../actions/types';
import { parseActionNames, parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'dh-button';

/**
 * A flexible and accessible button component supporting native tags (`button`, `a`, `input`, `span`),
 * icons with SSR inline support, loading/disabled states, full-width mode, and declarative action triggers.
 *
 * Uses BEM methodology for predictable CSS architecture. Integrates with the actions system
 * for declarative user interactions (e.g., opening popups, navigating).
 *
 * @slot default - {required}, {content}, string
 * @example
 *   <dh-button variant="primary" icon="arrows--chevron-right" icon-position="after">Get Started</dh-button>
 */
@Component({
  tag: 'dh-button',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhButton {
  @Element() host: HTMLElement;

  /**
   * Which HTML tag to render.
   * - `button` (default) — standard interactive button
   * - `a` — navigation link styled as button
   * - `span` — non-semantic wrapper (adds role="button" + keyboard support)
   * - `input` — form input button
   * @default button
   */
  @Prop() tag: 'span' | 'a' | 'button' | 'input' = 'button';

  /**
   * Button visual style variant.
   * - `primary` — filled brand color (CTA)
   * - `secondary` — outlined with neutral border
   * - `tertiary` — filled neutral (black in light mode, white in inverted)
   * - `link` — text-only with underline on hover
   * @default primary
   */
  @Prop() variant: 'primary' | 'secondary' | 'tertiary' | 'link' = 'primary';

  /**
   * Size modifier.
   * - `sm` — compact padding
   * - `md` — default padding (mobile-first, expands on desktop)
   * @default md
   */
  @Prop() size: 'sm' | 'md' = 'md';

  /**
   * HTML `type` attribute for `<button>` or `<input>`.
   */
  @Prop() type?: 'button' | 'submit';

  /**
   * If provided, the button renders as `<a>` with this href.
   * When tag="a" is explicitly set, href defaults to empty string for SEO.
   */
  @Prop() href?: string;

  /**
   * Disables the button. Applies `disabled` attribute or `aria-disabled`
   * depending on the rendered tag.
   * @default false
   */
  @Prop() disabled: boolean = false;

  /**
   * Title text shown as native browser tooltip on hover.
   * Also used as fallback aria-label for icon-only buttons.
   */
  @Prop() buttonTitle?: string;

  /**
   * Accessibility label for screen readers.
   * Especially useful for icon-only buttons.
   */
  @Prop() ariaLabel?: string;

  /**
   * Icon name to render inside the button via `<dh-icon>`.
   * Supports name-based ('arrows--chevron-right'), URL, or inline SVG.
   * @content
   */
  @Prop() icon?: string;

  /**
   * Position of icon relative to text content.
   * - `before` — icon left of text
   * - `after` — icon right of text
   * - `only` — icon only, text hidden (use ariaLabel for a11y)
   * @default before
   */
  @Prop() iconPosition: 'before' | 'after' | 'only' = 'before';

  /**
   * Inline the icon SVG during server-side rendering (SSR).
   * When true, dh-icon renders with `data-inline-icon` for the server post-processor.
   * @default false
   */
  @Prop() iconInline: boolean = false;

  /**
   * Make the button span the full width of its container.
   * @default false
   */
  @Prop() fullWidth: boolean = false;

  /**
   * Show loading state. Disables interaction and shows a loading animation.
   * @default false
   */
  @Prop() loading: boolean = false;

  /**
   * Specifies the target attribute for `<a>` tag (e.g., _blank, _self).
   */
  @Prop() target?: string;

  /**
   * Specifies the rel attribute for `<a>` tag (e.g., noopener, noreferrer).
   */
  @Prop() rel?: string;

  /**
   * Array of action names to execute on click.
   * Format: 'category:action-name' (e.g., 'ui:open-popup', 'nav:redirect').
   */
  @Prop({ reflect: true, attribute: 'trigger-actions' }) triggerActions?: ActionName[] | string = [];

  /**
   * Additional data to pass to actions.
   * Keys correspond to action names; values are action-specific config objects.
   *
   * `@type {object}` is load-bearing for the generated schema, not decoration: the schema
   * generator has no case for an inline TS object type, so both arms of
   * `Record<string, any> | string` fell through to its string default and the prop was
   * published as `oneOf: [string, string]` — a contradiction of what the render payload
   * actually delivers, which is an object.
   * @type {object}
   */
  // Not reflected on purpose: reflection stringifies with String(value), which for an object
  // is "[object Object]" — the attribute would then parse back to {} and lose the data.
  @Prop({ attribute: 'action-data' }) actionData?: Record<string, any> | string = {};

  /**
   * Action data, tolerating the JSON-string form that arrives as an attribute on
   * server-rendered pages as well as the object form from a property binding.
   */
  private resolveActionData(): Record<string, any> {
    return parseJsonProp<Record<string, any>>(this.actionData, {}) ?? {};
  }


  @State() computedLabel = '';
  @State() hasSlotContent = false;

  private renderedEl!: HTMLElement;

  private getIconPosition(): 'before' | 'after' | 'only' {
    const attr = this.host.getAttribute('icon-position');
    if (attr === 'after' || attr === 'only' || attr === 'before') return attr;
    return this.iconPosition;
  }

  private getComputedAriaLabel(): string | undefined {
    if (this.ariaLabel) return this.ariaLabel;
    if (this.icon && this.getIconPosition() === 'only') {
      return this.buttonTitle || this.host.textContent?.trim() || this.computedLabel;
    }
    return undefined;
  }

  componentWillRender() {
    const rawText = this.host.textContent?.trim();
    this.hasSlotContent = !!rawText;

    if (this.tag === 'input') {
      this.computedLabel = rawText || '';
    }

    if (this.icon && this.getIconPosition() === 'only') {
      if (rawText) {
        this.host.innerHTML = '';
      }
      this.hasSlotContent = false;
    }
  }

  componentDidLoad() {
    this.updateSpanKeyListener();
    this.attachClickListener();
  }

  componentDidUpdate() {
    this.updateSpanKeyListener();
    this.attachClickListener();
  }

  disconnectedCallback() {
    this.detachClickListener();
    this.host.removeEventListener('keydown', this.handleKeyDown);
  }

  private updateSpanKeyListener() {
    if (this.tag === 'span') {
      if (!this.disabled && !this.loading) {
        this.host.addEventListener('keydown', this.handleKeyDown);
      } else {
        this.host.removeEventListener('keydown', this.handleKeyDown);
      }
    }
  }

  /**
   * Returns the inner HTML element (`<button>`, `<a>`, etc.).
   * Can be used by parent components to control focus or attributes.
   */
  @Method()
  async getInnerElement(): Promise<HTMLElement | undefined> {
    return this.renderedEl;
  }

  private async executeActions(event: MouseEvent): Promise<void> {
    const actionNames = parseActionNames(this.triggerActions) as ActionName[];
    if (!actionNames.length) return;

    try {
      for (const actionName of actionNames) {
        await this.executeSingleAction(actionName, event);
      }
    } catch (error) {
      console.error('Action execution failed:', error);
    }
  }

  private async executeSingleAction(actionName: ActionName, event: MouseEvent): Promise<void> {
    const actionLoader = ACTION_REGISTRY[actionName];

    if (!actionLoader) {
      console.warn(`Action "${actionName}" not found in registry`);
      return;
    }

    try {
      const actionModule = await actionLoader();
      const ActionClass = actionModule.default;
      const action = new ActionClass();

      const context: ActionContext = {
        event,
        element: this.renderedEl,
        component: this,
        data: this.resolveActionData()[actionName] ?? this.resolveActionData(),
      };

      await action.execute(context);
    } catch (error) {
      console.error(`Failed to execute action "${actionName}":`, error);
      throw error;
    }
  }

  private handleClick = async (event: MouseEvent) => {
    if (this.disabled || this.loading) {
      event.preventDefault();
      return;
    }

    if (parseActionNames(this.triggerActions).length) {
      await this.executeActions(event);
    }
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (this.disabled || this.loading) return;
    if (event.key === 'Enter' || event.key === ' ') {
      this.host.click();
      event.preventDefault();
    }
  };

  private getClassList(): string {
    const hasIcon = !!this.icon;
    const iconPos = this.getIconPosition();
    const iconOnly = hasIcon && iconPos === 'only' && !this.hasSlotContent;

    const classes: Record<string, boolean> = {
      [COMPONENT_NAME]: true,
      [`${COMPONENT_NAME}--${this.variant}`]: true,
      [`${COMPONENT_NAME}--${this.size}`]: this.size !== 'md',
      [`${COMPONENT_NAME}--disabled`]: this.disabled,
      [`${COMPONENT_NAME}--loading`]: this.loading,
      [`${COMPONENT_NAME}--full-width`]: this.fullWidth,
      [`${COMPONENT_NAME}--icon-${iconPos}`]: hasIcon,
      [`${COMPONENT_NAME}--icon-only`]: iconOnly,
    };

    return Object.entries(classes)
      .filter(([, value]) => value)
      .map(([key]) => key)
      .join(' ');
  }

  private getAttributes(tag: string): Record<string, any> {
    const attrs: Record<string, any> = {
      'class': this.getClassList(),
      'title': this.buttonTitle,
      'aria-label': this.getComputedAriaLabel(),
    };

    if (this.loading) {
      attrs['aria-busy'] = 'true';
    }

    if (tag === 'span') {
      attrs.role = 'button';
      attrs.tabIndex = (this.disabled || this.loading) ? -1 : 0;
      if (this.disabled) attrs['aria-disabled'] = 'true';
    }

    if (tag === 'a') {
      attrs.href = this.href !== undefined && this.href !== null ? this.href : '';
      attrs.target = this.target;
      attrs.rel = this.rel;
      if (this.disabled) {
        attrs['aria-disabled'] = 'true';
        attrs.tabIndex = -1;
      }
    }

    if (tag === 'button' || tag === 'input') {
      attrs.disabled = this.disabled || this.loading;
    }

    if (tag === 'button' && this.type) {
      attrs.type = this.type;
    }

    if (tag === 'input') {
      attrs.type = this.type || 'button';
      attrs.value = this.computedLabel;
    }

    return attrs;
  }

  private attachClickListener() {
    if (!this.renderedEl) return;
    this.detachClickListener();
    this.renderedEl.addEventListener('click', this.handleClick);
  }

  private detachClickListener() {
    if (!this.renderedEl) return;
    this.renderedEl.removeEventListener('click', this.handleClick);
  }

  private renderIcon(position: 'before' | 'after' | 'only') {
    if (!this.icon || this.getIconPosition() !== position) return null;

    return (
      <dh-icon
        name={this.icon}
        class={`${COMPONENT_NAME}__icon`}
        lazy={!this.iconInline}
        inline={this.iconInline}
      />
    );
  }

  render() {
    const Tag = this.tag === 'a' ? 'a' : this.href ? 'a' : this.tag || 'button';
    const attrs = this.getAttributes(Tag);
    const isIconOnly = this.icon && this.getIconPosition() === 'only' && !this.hasSlotContent;
    const wrapText = this.variant === 'link';

    return Tag === 'input' ? (
      <Tag
        ref={el => { this.renderedEl = el; }}
        {...attrs}
      />
    ) : (
      <Tag
        ref={el => { this.renderedEl = el; }}
        {...attrs}
      >
        {!isIconOnly && this.renderIcon('before')}
        {!isIconOnly && (wrapText
          ? <span class={`${COMPONENT_NAME}__text`}><slot /></span>
          : <slot />
        )}
        {!isIconOnly && this.renderIcon('after')}
        {isIconOnly && this.renderIcon('only')}
      </Tag>
    );
  }
}
